import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import ProjectNav from "@/components/feature/ProjectNav";
import FrameOverlay from "@/components/base/FrameOverlay";
import FadeIn from "@/components/base/FadeIn";
import TiltCard from "@/components/base/TiltCard";
import {
  artistPortfolioImages,
  portfolioWorks,
  renaissanceWorks,
  monaWorks,
  mortalWorks,
} from "@/mocks/portfolio";

interface GalleryItem {
  id: string;
  image: string;
  aspect: string;
  artist: string;
  collection: string;
}

function buildGallery(): GalleryItem[] {
  const items: GalleryItem[] = [];

  const artists = [
    { key: "marcus-aurelius", name: "Juan Ramirez", collection: "Artist Portfolio" },
    { key: "isabella-rossi", name: "Isabella Rossi", collection: "Artist Portfolio" },
    { key: "cassius-vel", name: "Cassius Vel", collection: "Artist Portfolio" },
    { key: "seraphina-dor", name: "Seraphina Dor", collection: "Artist Portfolio" },
  ];

  artists.forEach(({ key, name, collection }) => {
    (artistPortfolioImages[key] || []).forEach((w) => {
      items.push({ id: w.id, image: w.image, aspect: w.aspect, artist: name, collection });
    });
  });

  portfolioWorks.forEach((w) => {
    items.push({ id: w.id, image: w.image, aspect: w.aspect, artist: w.artist, collection: "Portfolio" });
  });

  renaissanceWorks.forEach((w) => {
    items.push({ id: w.id, image: w.image, aspect: w.aspect, artist: "Collective", collection: "Re:Naissance" });
  });

  monaWorks.forEach((w) => {
    items.push({ id: w.id, image: w.image, aspect: "aspect-square", artist: "Collective", collection: "Many Mona" });
  });

  mortalWorks.forEach((w) => {
    items.push({ id: w.id, image: w.image, aspect: "aspect-square", artist: "Collective", collection: "Mmnto.Mori" });
  });

  return items;
}

const FILTERS = [
  { label: "All Works", value: "all" },
  { label: "Juan Ramirez", value: "Juan Ramirez" },
  { label: "Isabella Rossi", value: "Isabella Rossi" },
  { label: "Cassius Vel", value: "Cassius Vel" },
  { label: "Seraphina Dor", value: "Seraphina Dor" },
  { label: "Collective", value: "Collective" },
];

export default function GalleryPage() {
  const allWorks = buildGallery();
  const [activeFilter, setActiveFilter] = useState("all");

  const filtered =
    activeFilter === "all"
      ? allWorks
      : allWorks.filter((w) => w.artist === activeFilter);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Header />
      <main>
        <section className="bg-background-50 pt-32 md:pt-40 pb-24 md:pb-36">
          <div className="px-6 md:px-12">
            {/* Heading */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
              <div>
                <p className="font-display text-xs uppercase tracking-[0.25em] text-foreground-700 mb-4">
                  ( Gallery ) — The Complete Archive
                </p>
                <h1 className="font-display font-black text-foreground-950 leading-[0.9] tracking-[-0.04em] text-[14vw] md:text-[10vw] lg:text-[8vw] whitespace-nowrap">
                  <span className="title-caps">The</span>
                  <em className="font-serif italic font-light title-italic"> Atelier</em>
                  <br />
                  <span className="title-caps">Collection</span>
                </h1>
              </div>
              <div className="max-w-md">
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed font-serif">
                  Every piece across our four master artists, studio collections, and collaborative projects — gathered in one sacred archive.
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-6 md:gap-10 mb-14 pb-10 border-b border-foreground-950/15">
              <div>
                <p className="font-display font-black text-4xl md:text-5xl text-foreground-950 leading-none">{allWorks.length}</p>
                <p className="font-display text-[10px] uppercase tracking-[0.22em] text-foreground-700 mt-2">Total Works</p>
              </div>
              <div>
                <p className="font-display font-black text-4xl md:text-5xl text-foreground-950 leading-none">4</p>
                <p className="font-display text-[10px] uppercase tracking-[0.22em] text-foreground-700 mt-2">Master Artists</p>
              </div>
              <div>
                <p className="font-display font-black text-4xl md:text-5xl text-foreground-950 leading-none">5</p>
                <p className="font-display text-[10px] uppercase tracking-[0.22em] text-foreground-700 mt-2">Collections</p>
              </div>
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap items-center gap-2 mb-12" data-product-shop>
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setActiveFilter(f.value)}
                  className={`px-4 py-2 rounded-full font-display text-xs uppercase tracking-[0.18em] transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeFilter === f.value
                      ? "bg-foreground-950 text-background-50 border border-foreground-950"
                      : "border border-foreground-950/30 text-foreground-950 hover:border-foreground-950/60"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Gallery grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
              {filtered.map((w, i) => (
                <FadeIn key={`${w.id}-${i}`} delay={i * 50} className="group cursor-pointer">
                  <TiltCard className={`${w.aspect} bg-background-100 overflow-hidden mb-3 relative`}>
                    <img
                      src={w.image}
                      alt={`${w.id} by ${w.artist}`}
                      className="w-full h-full object-cover object-top"
                    />
                    <FrameOverlay />
                  </TiltCard>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="font-display text-[11px] md:text-xs uppercase tracking-[0.2em] text-foreground-950 block whitespace-nowrap">
                        {w.id}
                      </span>
                      <span className="font-serif italic text-[11px] text-foreground-700">
                        {w.artist}
                      </span>
                    </div>
                    <span className="font-display text-[9px] uppercase tracking-[0.18em] text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {w.collection}
                    </span>
                  </div>
                </FadeIn>
              ))}
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="text-center py-24">
                <p className="font-serif italic text-xl text-foreground-700">
                  No works found for this filter.
                </p>
              </div>
            )}

            {/* Booking CTA */}
            <div className="mt-20 pt-12 border-t border-foreground-950/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <p className="font-display text-sm uppercase tracking-[0.15em] text-foreground-950 mb-2">Inspired by What You See?</p>
                <p className="text-foreground-700 font-serif text-sm">Book a consultation with any of our master artists.</p>
              </div>
              <Link
                to="/"
                state={{ faScrollTo: "booking" }}
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 text-background-50 font-display text-xs uppercase tracking-[0.2em] rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Book Consultation
                <i className="ri-arrow-right-up-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <ProjectNav
          prev={{ label: "Home", to: "/" }}
          next={{ label: "Juan Ramirez", to: "/artist/marcus-aurelius" }}
        />
      </main>
      <Footer />
    </div>
  );
}